import { useCurrentReflection } from '@/hooks'
import { useRouteModalHistory } from '@/router'
import { selectedRoleAtom } from '@/store'
import { semesterInfoAtom } from '@/store/atom'
import { SelectedRoleEnum } from '@/store/types'
import { initStoreRoleAndGraduatesTermName } from '@/utils/storage'
// import { Moment, moment } from '@seiue/moment'
import { Button, Select, State } from '@seiue/ui'
import { useAtomValue, useSetAtom } from 'jotai'
import { $t } from 'packages/locale'
import React, { useEffect, useState } from 'react'
import styled, { useTheme } from 'styled-components'

import { StatsDetailsTabEnum, TimeRange } from '../../types'
import { useSemesterInfo } from '../../utils/hooks'
import { Flex } from '../components/Styled'

import { AbsenceCard } from './StatsCard/AbsenceCard'
import { AcademicAffairsCard } from './StatsCard/AcademicAffairsCard'
import { AttendanceCard } from './StatsCard/AttendanceCard'
import { DeviceStatsCard } from './StatsCard/DeviceStatsCard'
import { LiftViolationCard } from './StatsCard/LiftViolationCard'
import { PadViolationCard } from './StatsCard/PadViolationCard'
import { PunishmentCard } from './StatsCard/PunishmentCard'
import { SilentCard } from './StatsCard/SilentCard'
import { StudentDailyAttendance } from './StatsCard/StudentDailyAttendance'
import { TakeoutViolationCard } from './StatsCard/TakeoutViolationCard'
import { TeacherAttendanceCard } from './StatsCard/TeacherAttendanceCard'
import { DataBoardContext } from './context'
import { getReadAllButtonType } from './utils/content'
import {
  useHasOnlyPadManagementPermission,
  useHasStudentPermission,
  useHideRecentCard,
} from './utils/hooks'

/**
 * 数据看板
 *
 * @returns  数据看板
 */
export const DataBoard: React.FC = () => {
  const theme = useTheme()
  const history = useRouteModalHistory()
  const [timeRange, setTimeRange] = useState<TimeRange>('week')

  const role = useAtomValue(selectedRoleAtom)
  // const [date, setDate] = useState<Moment>(moment())

  const setSemesterInfoAtom = useSetAtom(semesterInfoAtom)
  const reflection = useCurrentReflection()
  const hasStudentPermission = useHasStudentPermission()
  const hideRecentCard = useHideRecentCard()
  // 只有平板管理权限，只展示平板违规统计卡片和平板管理的详情
  const hasOnlyPadManagementPermission = useHasOnlyPadManagementPermission()

  const { semesterInfo, loading } = useSemesterInfo()
  useEffect(() => {
    if (!!semesterInfo) {
      setSemesterInfoAtom(semesterInfo)
    }
  }, [semesterInfo])

  useEffect(() => {
    if (semesterInfo) {
      initStoreRoleAndGraduatesTermName(semesterInfo)
    }
  }, [semesterInfo])

  if (loading) return <State.Loading />

  const empty = !semesterInfo || !role

  if (empty) return null

  const showDetailsButton = [
    SelectedRoleEnum.Mentor,
    SelectedRoleEnum.EducationalSupervisor,
    SelectedRoleEnum.SchoolLeaders,
    SelectedRoleEnum.Dean,
    SelectedRoleEnum.Yuke,
    SelectedRoleEnum.AdminclassMentor,
  ].includes(role)

  const selectedMentor = role === SelectedRoleEnum.Mentor
  const selectedAcademicAffairs = role === SelectedRoleEnum.AcademicAffairs
  const selectedDean = role === SelectedRoleEnum.Dean
  const selectedAdminclassMentor = role === SelectedRoleEnum.AdminclassMentor

  const showPadCard = selectedMentor || selectedDean || selectedAdminclassMentor

  const recentBoardCards = hasOnlyPadManagementPermission ? (
    <PadViolationCard style={{ width: 380, maxWidth: 380, minWidth: 380 }} />
  ) : hasStudentPermission && !hideRecentCard ? (
    <>
      <AttendanceCard />
      {!selectedDean ? <AbsenceCard /> : null}
      {showPadCard ? <PadViolationCard /> : null}
      <DeviceStatsCard />
      <PunishmentCard />
      {selectedDean ? (
        <TeacherAttendanceCard
          style={
            !hasStudentPermission ? { maxWidth: 'fit-content' } : undefined
          }
        />
      ) : null}
      <TakeoutViolationCard />
      <LiftViolationCard />
      <SilentCard />
    </>
  ) : null

  const contentMap = {
    [SelectedRoleEnum.Mentor]: recentBoardCards,
    [SelectedRoleEnum.EducationalSupervisor]: recentBoardCards,
    [SelectedRoleEnum.SchoolLeaders]: recentBoardCards,
    [SelectedRoleEnum.Dean]: recentBoardCards,
    [SelectedRoleEnum.Yuke]: recentBoardCards,
    [SelectedRoleEnum.AdminclassMentor]: recentBoardCards,
    [SelectedRoleEnum.AcademicAffairs]: <AcademicAffairsCard />,
  }

  // hack
  const readAllLabel =
    reflection?.usin === 'F0000G2206' ? $t('教师近况详情') : $t('数据详情')

  return (
    <DataBoardContext.Provider
      value={{
        timeRange,
        semesterInfo,
        role,
      }}
    >
      <Wrapper $noGap={hideRecentCard}>
        <Header>
          {hideRecentCard ? null : (
            <Flex>
              {selectedAcademicAffairs ? null : (
                <>
                  <TimeRangeSelect
                    options={[
                      { label: $t('本周'), value: 'week' },
                      { label: $t('上周'), value: 'last-week' },
                      { label: $t('本月'), value: 'month' },
                      { label: $t('本学期'), value: 'semester' },
                    ]}
                    onChange={setTimeRange}
                    value={timeRange}
                    $width={120}
                  />
                  {/* <DatePicker
                  value={date}
                  onChange={(val) => {
                    val && setDate(val)
                  }}
                  allowClear={false}
                /> */}
                  {hasStudentPermission && !hasOnlyPadManagementPermission ? (
                    <StudentDailyAttendance />
                  ) : null}
                </>
              )}
            </Flex>
          )}
          {showDetailsButton ? (
            <Button
              icon="BarChartSolid"
              style={{
                backgroundColor: '#fff',
                borderRadius: '16px',
                color: theme.text._1,
                marginRight: 0,
              }}
              iconRight="GreaterThan"
              onClick={() => {
                history.push('GatewayStatsDetails', {
                  type: getReadAllButtonType(reflection?.usin || ''),
                  timeRange,
                })
              }}
            >
              {readAllLabel}
            </Button>
          ) : null}
        </Header>
        <Content>{contentMap[role]}</Content>
      </Wrapper>
    </DataBoardContext.Provider>
  )
}

const Wrapper = styled.div<{ $noGap?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: ${({ $noGap }) => ($noGap ? 0 : 12)}px;
  width: 100%;
  min-width: 960px;
  max-width: 1600px;
`

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`

const Content = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 16px;

  /* 一行最多四个卡片，超出折行；每卡固定 1/4 行宽不压缩，四张时刚好撑满一行 */
  & > * {
    flex: 1 auto;
    min-width: calc((100% - 3 * 16px) / 4);
  }
`

const TimeRangeSelect = styled(Select<TimeRange>).attrs({
  allowClear: false,
  bordered: false,
})<{ $width?: number }>`
  background-color: #fff;
  border-radius: 8px;
  width: ${({ $width }) => $width}px;
`

const Tip = styled.div`
  font-size: 12px;
  color: ${(props) => props.theme.text._3};
`
